<template>
  <div v-if="short_term.loading || long_term.loading || cash_account.loading">
    Loading...
  </div>
  <div v-else>
    <p v-if="completed_plans.length > 1" style="font-size: 18px;">
      Use the dropdown below to review your completed plans.
    </p>
    <select 
      v-if="completed_plans.length >= 1"
      v-model="selectedPlan"
    >
      <option
        v-for="plan in completed_plans"
        :key="plan.id"
        :value="plan"
      >
        {{ plan.name }}
      </option>
    </select>
    <h3 class="completed">
      Completed
    </h3>
    <div v-show="statementMatchesPlan">
      <div class="video-and-progress">
        <div v-show="loadingLaunchVideo[selectedPlan?.id] == false && (selectedLaunchVideo?.src || (clientIsDiy && selectedPlanVideo))" class="videos-container">
          <video
            ref="launchVideoPlayer"
            class="launch-video-player"
            :style="!selectedLaunchVideo?.src ? 'display: none' : ''"
            preload="none"
            controls 
          >Your browser does not support the video tag.</video>
          <section
            v-if="loadingLaunchVideo[selectedPlan?.id] == false && !selectedLaunchVideo?.src && selectedPlanVideo"
            :key="selectedPlanVideo.video_id"
          >
            <div
              class="wistia_responsive_padding"
              style="padding:56.25% 0 0 0;position:relative;"
            >
              <div
                class="wistia_responsive_wrapper"
                style="height:100%;left:0;position:absolute;top:0;width:100%;"
              >
                <iframe
                  :src="`https://fast.wistia.net/embed/iframe/${selectedPlanVideo.video_id}?seo=false&videoFoam=true`"
                  :title="selectedPlanVideo.title"
                  allow="autoplay; fullscreen"
                  allowtransparency="true"
                  frameborder="0"
                  scrolling="no"
                  class="wistia_embed"
                  name="wistia_embed"
                  msallowfullscreen
                  width="100%"
                  height="100%"
                />
              </div>
            </div>

            <template v-if="selectedPlanVideo.link">
              <a :href="`${ selectedPlanVideo.link }`">Download</a>
            </template>
          </section>
        </div>
        <ShortTermPlanProgress
          v-if="statementMatchesPlan && selectedPlan.type == 'stip'"
          :key="`progress-${selectedPlan.id}`"
          :statement="selectedStatement"
        />
      </div>
      <template v-if="statementMatchesPlan">
        <template v-if="selectedPlan.type == 'stip'">
          <ShortTermPlanView
            v-if="selectedPlan.statement_version"
            :key="`plan-${selectedPlan.id}`"
            :plan="selectedStatement"
            :finalized="true"
          />
          <LegacyShortTermPlanView
            v-if="!selectedPlan.statement_version"
            :key="`plan-${selectedPlan.id}`"
            :plan="selectedStatement"
          />
        </template>
        <LongTermPlanView
          v-if="selectedPlan.type == 'ltip'"
          :key="`plan-${selectedPlan.id}`"
          :plan="selectedStatement"
          :finalized="true"
        />
        <AllocationPoolView
          v-if="selectedPlan.type == 'cap' && selectedStatement.current.summary_data.plan.funding_type == 'allocation-pool'"
          :key="`plan-${selectedPlan.id}`"
          :plan="selectedStatement"
          :certificates="selectedStatement.certificates"
          :finalized="true"
        />
        <CashAccountPlanView
          v-if="selectedPlan.type == 'cap' && selectedStatement.current.summary_data.plan.funding_type != 'allocation-pool'"
          :key="`plan-${selectedPlan.id}`"
          :plan="selectedStatement"
          :certificates="selectedStatement.certificates"
          :finalized="true"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
// imports
import ApiManager from '@/classes/ApiManager';
import DataDirectory from '@/classes/DataDirectory';
import { onBeforeRouteLeave } from 'vue-router';
import { ref, reactive, computed, watch, onBeforeMount, useTemplateRef } from 'vue';
import ShortTermPlanView from '@/components/pages/ShortTermPlanView.vue';
import LongTermPlanView from '@/components/pages/LongTermPlanView.vue';
import AllocationPoolView from '@/components/pages/AllocationPoolView.vue';
import CashAccountPlanView from '@/components/pages/CashAccountPlanView.vue';
import ShortTermPlanProgress from '@/components/plans/short-term/PlanProgress.vue';
import LegacyShortTermPlanView from "@/components/pages/LegacyShortTermPlanView.vue";

// variables
const $apiManager = new ApiManager();
const $dataDirectory = new DataDirectory();
const videos = ref([]);
const launchVideos = reactive({});
const loadingLaunchVideo = reactive({});
const launchVideoPlayer = useTemplateRef('launchVideoPlayer');
const selectedPlan = ref(null);
const planStatements = reactive({});
const short_term = reactive({
  loading: false,
  plans: {},
});
const long_term = reactive({
  loading: false,
  plans: {},
});
const cash_account = reactive({
  loading: false,
  plans: {},
});
const completed_plans = computed(() => {
  const aReturn = [];
  Object.values(short_term.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'stip'});
  });
  Object.values(long_term.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'ltip'});
  });
  Object.values(cash_account.plans).forEach((plan) => {
    if (plan.finalized) aReturn.push({...plan, type: 'cap'});
  });
  return aReturn;
});
const selectedStatement = computed(() => {
  return planStatements[selectedPlan.value?.id] ?? null;
});
const statementMatchesPlan = computed(() => {
  return (selectedStatement.value?.current.summary_data && (selectedStatement.value?.current.summary_data.plan_id == selectedPlan.value?.id || selectedStatement.value?.current.summary_data.plan?.id == selectedPlan.value?.id))
    || (selectedStatement.value?.current.statement_data && selectedStatement.value?.current.statement_data.plan.id == selectedPlan.value?.id);
})
const selectedPlanVideo = computed(() => {
    return videos.value.find((video) => video.plan_name == selectedPlan.value.name);
});
const selectedLaunchVideo = computed(() => {
  return launchVideos[selectedPlan.value?.id];
})
const clientIsDiy = computed(() => {
  return $dataDirectory.userData.client_status === 'diy' || $dataDirectory.userData.client_status === 'DIY';
});
// watchers
watch(completed_plans, () => {
  if (!selectedPlan.value) selectedPlan.value = completed_plans.value[0];
});
watch(selectedPlan, () => {
  console.log('selected plan %s, %ssrc %s', selectedPlan.value?.id ?? 'null', selectedLaunchVideo.value?.src && launchVideoPlayer.value.src != selectedLaunchVideo.value.src ? 'set ' : '', selectedLaunchVideo.value?.src ?? 'null');
  if (selectedLaunchVideo.value?.src && launchVideoPlayer.value?.src != selectedLaunchVideo.value.src) {
    launchVideoPlayer.value.src = selectedLaunchVideo.value.src;
    launchVideoPlayer.value.load();
  }
});
watch(loadingLaunchVideo, (newVal) => {
  if (!newVal[selectedPlan.value.id] && selectedLaunchVideo.value?.src && launchVideoPlayer.value?.src != selectedLaunchVideo.value.src) {
    launchVideoPlayer.value.src = selectedLaunchVideo.value.src;
    launchVideoPlayer.value.load();
    console.log('plan %s video loaded, set src %s', selectedPlan.value?.id ?? 'null', selectedLaunchVideo.value?.src ?? 'null');
  }
}, {deep: true});
watch(statementMatchesPlan, (newVal) => {
  console.log('statement %s, %slaunch video src %s', newVal ? 'loaded' : 'loading', selectedLaunchVideo.value?.src ? 'set ' : '', selectedLaunchVideo.value?.src ?? 'null');
  if (newVal && selectedLaunchVideo.value?.src && launchVideoPlayer.value?.src != selectedLaunchVideo.value.src) {
    launchVideoPlayer.value.src = selectedLaunchVideo.value.src;
    launchVideoPlayer.value.load();
  }
});
// functions
function getPlanVideos() {
  $apiManager.get(`plan-videos`)
    .then((result) => {
        if(result.data?.videos) videos.value = Object.values(result.data.videos);
    })
    .catch((error) => {
        console.error(error)
    })
}
/** Fetch video meta data if we do not have launch video for plan. */
function getLaunchVideo(plan) {
  if (!launchVideos[plan.id]?.src) {
    loadingLaunchVideo[plan.id] = true;
    fetch($apiManager.route("launch-video-meta/" + plan.id), {responseType: 'application/json', credentials: 'include'}).then(
      (pack) => {
        // success response will be json
        if (pack.headers.get('content-type') == 'application/json') {
          pack.json().then((data) => {
            if (data.success) {
              launchVideos[plan.id] = {
                src: $apiManager.route("launch-video/" + plan.id),
                type: data.type,
              };
              loadingLaunchVideo[plan.id] = false;
            } else {
              console.error(data);
              loadingLaunchVideo[plan.id] = false;
            }
          });
        } else {
          console.error('failed to get launch video metadata');
          loadingLaunchVideo[plan.id] = false;
        }
      },
      (err) => {
        console.error(err);
        loadingLaunchVideo[plan.id] = false;
      }
    );
  }
}
function getShortTermPlans() {
  short_term.loading = true
  $apiManager.get("plans/stip-list")
      .then((result) => {
        short_term.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'stip'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        short_term.loading = false
      })
}
function getLongTermPlans() {
  long_term.loading = true
  $apiManager.get("plans/ltip-list")
      .then((result) => {
        long_term.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'ltip'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        long_term.loading = false
      })
}
function getCashAccountPlans() {
  cash_account.loading = true
  $apiManager.get("plans/cap-list")
      .then((result) => {
        cash_account.plans = result.data.plans;
        Object.values(result.data.plans).forEach((plan) => {
          if (plan.finalized) {
            let planWithType = {...plan, type: 'cap'};
            getStatement(planWithType);
            getLaunchVideo(planWithType);
          }
        });
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        cash_account.loading = false
      })
}
function getStatement(plan) {
  $apiManager.get(`plans/${plan.type}/${plan.id}`)
    .then((result) => {
      planStatements[plan.id] = result.data;
    })
    .catch((error) => {
      console.error(error)
    })
}
onBeforeMount(() => {
  // ensure our session is valid and userData is initialized
  $dataDirectory.checkLogin(null, () => this.$router.push("/login"));
  getPlanVideos();
  // Get list of each active plan type and load all plan accessories
  getShortTermPlans();
  getLongTermPlans();
  getCashAccountPlans();
});

onBeforeRouteLeave(() => {
  // ensure the launch video is not loading
  if (launchVideoPlayer.value) {
    launchVideoPlayer.value.src = '';
    launchVideoPlayer.value.load();
  }
});
</script>

<style scoped lang="scss">
@use "../../scss/_variables.scss";
.video-and-progress {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  @media screen and (max-width: 850px){
    flex-direction: column;
    gap: 8px;
  }
}
.launch-video-player {
  max-width: 100%;
  max-height: 472px;
}
.videos-container {
  width: 100%;
  @media screen and (min-width: 851px) {
    width: 49%;
  }
}
select {
  font-size: 22px;
  font-weight: bold;
}
h3.completed {
  padding-left: 22px;
  margin-bottom: 1rem;
}
</style>